/**
 *  This is the top level component class that is responsible for managing the state of
 *  it's underlying sub-components
 */

// Libs
import React, {Component} from 'react';
import classnames from 'classnames';

// Functions
import {addRecentSearch, pruneLocalStorage} from './functions/recent';
import {keypress, isTabOutsideTypeahead} from './functions/keyboard';
import {
    isDestinationChanged,
    isSubDestinationChanged,
    getDestinationTerm,
    getFilterName,
    isListingIdSearchTurnedOn
} from './functions/destinationHelper';
import {getSelectedResult} from './functions/resultsNavigator';
import {focusSearchInput, getHintSuggestion} from './functions/searchinput';
import {
    emitGE,
    emitSelectionEvent,
    emitFocusEvent,
    emitStartEvent
} from './functions/edap';
import {buildSelectionEventPayload} from './functions/edapPayload';
import {
    resetAnalytics,
    getSelectionDepth,
    incrementNoSelectionCounter
} from './functions/analytics';
import {skipFilters} from './functions/filters';

// Data
import {TypeaheadStore} from './store';
import {UISTATES} from './enum';

// View components
import {Typeahead} from './components/typeahead';
import {AriaStatus} from './components/ariastatus';
import {SvgDefinitions} from './components/svgdefinitions';
import {isListingIdSearch} from './functions/destinationHelper';

// Style
import './container.less';

const PIDRegExp = /^(p\d+)/;

class TypeaheadContainer extends Component {
    /* istanbul ignore next */
    constructor(props) {
        super(props);

        // Default state
        // This is where we apply global context to the component
        this.state = {
            uistate: UISTATES.CLOSED,
            results: false,
            xhrPending: false,
            searchSuggestion: '',
            selectedDestination: null,
            selectedSubDestination: null,
            selectDestinationIndex: -1,
            selectDestinationSubIndex: -1,
            used: false,
            isTypeaheadInputFocus: false,
            ...this.props
        };

        this.analytics = {
            backspaceCounter: 0,
            userInputLength: 0,
            noSelectionCounter: 0,
            prevSearch: null,
            resultsUuid: null,
            selectionDepth: 1 // 1-based depth
        };

        this.trending = {
            results: null,
            isLoading: false,
            isTypeaheadFocused: false
        };
    }

    componentDidMount() {
        // Initialize the typeahead store /w initial state as configuration
        this.store = new TypeaheadStore(this.props.config);

        const {search} = this.state;

        // Remove old/invalid entries from localStorage
        pruneLocalStorage();

        // If value is set, pre-warm typeahead results
        if (search && search.length > 0) {
            this.analytics.prevSearch = search;
            this.store.getResults(search, this.props.filterType, () => {});
        }

        // add window click handler to close pop ups
        window.addEventListener(
            'click',
            this.goToClosedStateIfClickedOutside,
            true
        );
        window.addEventListener(
            'touchstart',
            this.goToClosedStateIfClickedOutside,
            true
        );
    }

    /**
     * componentWillReceiveProps()
     * @param {*} nextProps
     */
    componentWillReceiveProps(nextProps) {
        this.loadTrendingResults(nextProps);

        // Sync filtered list of new props to sync with state.
        let nextState = {};
        /* istanbul ignore next */
        Object.keys(nextProps).forEach((prop) => {
            if (
                nextProps[prop] !== this.props[prop] &&
                nextProps[prop] !== this.state[prop]
            ) {
                let nextStateValue = nextProps[prop];
                if (prop === 'search') {
                    // If parent context updates "search", invalidate selectedDestination in order to render search text.
                    this.setState({
                        selectedDestination: null,
                        results: false
                    });
                }
                nextState[prop] = nextStateValue;
            }
        });
        if (Object.keys(nextState).length > 0) {
            this.setState({...nextState});
            return true;
        }
        return false;
    }

    componentDidUpdate(nextProps, nextState) {
        const {noSelectionCounter} = this.analytics;
        const {results} = this.state;
        this.analytics.noSelectionCounter = incrementNoSelectionCounter(
            results,
            nextState.results,
            noSelectionCounter
        );
    }

    loadTrendingResults = ({showTrendingSuggestions}) => {
        const {results, isLoading, isTypeaheadFocused} = this.trending;
        if (showTrendingSuggestions && !results && !isLoading) {
            this.trending.isLoading = true;
            this.store.getTrending(({resultsWithHighlights}) => {
                this.trending = {
                    results: resultsWithHighlights,
                    isLoading: false
                };
                if (isTypeaheadFocused) {
                    this.handleFocus();
                }
            });
        }
    };

    /**
     * handleError()
     * Handle errors in child components
     */
    handleError = (msg) => {
        console.error(msg); // eslint-disable-line no-console
        return emitGE({
            eventaction: 'error',
            eventvalue: msg,
            eventlabel: ''
        });
    };

    /**
     *  handleTopLevelKeyEvents()
     *  Handle top level key events to implement keyboard navigation
     */
    handleTopLevelKeyEvents = (ev) => {
        const result = keypress(this.state, ev);
        const {
            results,
            search,
            selectDestinationIndex,
            selectDestinationSubIndex
        } = this.state;

        // true: keypress() resulted in selected destination
        if (result === true || isTabOutsideTypeahead(this.state, ev)) {
            this.analytics.selectionDepth = getSelectionDepth(
                selectDestinationIndex
            );
            const selectedResult = getSelectedResult(
                results,
                selectDestinationIndex,
                selectDestinationSubIndex
            );
            if (!isTabOutsideTypeahead(this.state, ev)) {
                this.handleSelectDestination(
                    selectedResult.destination || {term: search},
                    selectedResult.subDestination
                );
            } else {
                setTimeout(() => {
                    this.handleSelectDestination(
                        selectedResult.destination || {term: search},
                        selectedResult.subDestination
                    );
                }, 10);
            }
        } else if (result) {
            // control key press ESC, Up, Down
            this.setState(result);
            this.handleFocus();
        }
        return result;
    };

    /**
     * handleOnMouseOver()
     * Handle on mouse over to reset keyboard navigation state
     */
    handleOnMouseOver = () => {
        if (this.state.selectDestinationIndex !== -1) {
            this.setState({selectDestinationIndex: -1});
        }
        if (this.state.uistate !== UISTATES.CLOSED) {
            this.handleFocus();
            return true;
        }
        return false;
    };

    /**
     * goToClosedStateIfClickedOutside()
     * if click outside of component close any open popups
     */
    goToClosedStateIfClickedOutside = (e) => {
        let {
            isTypeaheadInputFocus,
            results,
            selectDestinationIndex,
            selectDestinationSubIndex,
            search
        } = this.state;
        if (
            this.containerEl &&
            !this.containerEl.contains(e.target) &&
            isTypeaheadInputFocus
        ) {
            const selectedResult = getSelectedResult(
                results,
                selectDestinationIndex,
                selectDestinationSubIndex
            );
            this.handleSelectDestination(
                selectedResult.destination || {term: search},
                selectedResult.subDestination
            );
            return true;
        }
        return false;
    };

    /**
     * handleTypeaheadClick()
     */
    handleTypeaheadClick = () => {
        this.setState({
            uistate: UISTATES.SELECT_DESTINATION,
            isTypeaheadInputFocus: true
        });
    };

    shouldUseTrendingResults = () => {
        const {search} = this.state;
        const {showTrendingSuggestions} = this.props;
        return !search && showTrendingSuggestions;
    };

    getCurrentTarget = (e) => {
        return e && e.currentTarget;
    };

    /**
     * handleFocus()
     * Handles when input receives focus
     */
    handleFocus = (e) => {
        this.trending.isTypeaheadFocused = true;
        const state = focusSearchInput(this.state);
        const target = this.getCurrentTarget(e);
        if (target) {
            // setTimeout is needed because some browsers eg. MS Edge are unable to select synchronously
            setTimeout(() => target.select(), 50);
        }
        if (state) {
            // If user makes first focus and search input is empty then fetch trending results
            if (this.shouldUseTrendingResults()) {
                this.setState({
                    results: this.trending.results,
                    isTrendingResults: true
                });
                return;
            }
            // If results is not initialized, fetch em
            this.state.uistate = state.uistate; // eslint-disable-line react/no-direct-mutation-state
            if (this.state.results === false) {
                this.handleSearchInputChange();
            }

            // Make sure to update to open state
            this.setState({
                ...state,
                isTypeaheadInputFocus: true
            });
        }

        this.triggerFocusEdap();
        this.props.onFocus();
    };

    triggerFocusEdap() {
        if (!this.state.wasFirstFocus) {
            this.setState({wasFirstFocus: true});
            emitFocusEvent(this.state.search || null);
        }
    }

    triggerStartEdap() {
        if (!this.state.wasFirstInput) {
            this.setState({wasFirstInput: true});
            emitStartEvent(this.state.search || null);
        }
    }

    triggerUsedEdap = (userInput) => {
        if (userInput.length && !this.state.used) {
            emitGE({eventaction: 'used'});
            this.setState({used: true});
        }
    };

    triggerSelectionEdap = (destination, subDestination) => {
        const {analytics} = this;
        const {results, search} = this.state;

        if (destination && !destination.uuid) {
            emitGE({
                eventaction: 'selected-destination-with-no-results',
                eventlabel: null,
                eventvalue: destination.term
            });
        } else {
            const sePayload = buildSelectionEventPayload({
                destination,
                subDestination,
                results,
                userInput: search,
                analytics
            });
            emitSelectionEvent(sePayload);
        }
    };

    getFirstResultTerm = (results) => {
        return results && results[0] && results[0].term;
    };

    getUserInput = (event) => {
        return (event ? event.target.value : this.state.search) || '';
    };

    getSearchSuggestion = (userInput, term) => {
        const {showInlineSuggestion} = this.props;
        return showInlineSuggestion ? getHintSuggestion(userInput, term) : '';
    };

    fetchResults = (userInput) => {
        const {filterType} = this.props;
        this.store.getResults(userInput, filterType, (searchResults) => {
            if (!isListingIdSearchTurnedOn()) {
                let {results, resultsWithHighlights} = searchResults;
                results = results.filter(
                    ({matchType}) => matchType !== 'LISTING'
                );
                resultsWithHighlights = resultsWithHighlights.filter(
                    ({matchType}) => matchType !== 'LISTING'
                );
                searchResults = {
                    ...searchResults,
                    results,
                    resultsWithHighlights
                };
            }

            // Update post-fetch state
            const firstTerm = this.getFirstResultTerm(searchResults.results);
            this.setState({
                searchSuggestion: this.getSearchSuggestion(
                    userInput,
                    firstTerm
                ),
                results: searchResults.resultsWithHighlights,
                poweredByGoogle: searchResults.poweredByGoogle,
                xhrPending: false,
                isTrendingResults: false
            });
            this.analytics.resultsUuid = searchResults.resultsUuid;
        });
    };

    /**
     * handleSearchInputChange()
     * Destination Input Change event handler
     */
    /* eslint-disable complexity */
    handleSearchInputChange = (ev) => {
        let userInput = this.getUserInput(ev);
        const firstTerm = this.getFirstResultTerm(this.state.results);

        if (
            (!this.analytics.prevSearch && userInput.length > 0) ||
            (this.analytics.prevSearch &&
                this.analytics.prevSearch.length !== userInput.length)
        ) {
            this.triggerStartEdap();
        }

        // Increment "backspace" counter if user delete character
        if (userInput.length < this.analytics.userInputLength) {
            this.analytics.backspaceCounter++;
        }

        this.analytics.userInputLength = userInput.length;

        // Trim preceding whitespace
        userInput = userInput.replace(/^\s(.*)/, '$1');

        // Emit a `used` event (one per pageview)
        this.triggerUsedEdap(userInput);

        // If userInput is empty, set an empty state
        if (
            !userInput ||
            userInput.length === 0 ||
            (PIDRegExp.test(userInput) && !isListingIdSearchTurnedOn())
        ) {
            if (this.props.showTrendingSuggestions) {
                this.setState({
                    results: this.trending.results,
                    search: '',
                    searchSuggestion: '',
                    isTrendingResults: true,
                    isTypeaheadInputFocus: true
                });
                return false;
            }
            this.setState({
                results: false,
                search: userInput,
                searchSuggestion: '',
                selectedDestination: null,
                selectedSubDestination: null,
                isTypeaheadInputFocus: true
            });
            this.store.getResults(false); // Send signal to store that user has changed input
            return false;
        }
        this.setState({
            search: userInput,
            searchSuggestion: this.getSearchSuggestion(userInput, firstTerm),
            isTypeaheadInputFocus: true,
            uistate: UISTATES.SELECT_DESTINATION,
            xhrPending: true
        });

        // fetch results from store (either via XHR or cache)
        this.fetchResults(userInput);
        return true;
    };
    /* eslint-enable complexity */

    /**
     * close()
     * Transition to closed state
     */
    close = () => {
        if (this.state.uistate !== UISTATES.CLOSED) {
            this.setState({uistate: UISTATES.CLOSED});
        }
    };

    /**
     * handleSelectDestination()
     * Update destination state
     */
    handleSelectDestination = (_destination, _subDestination) => {
        const {analytics} = this;
        const {search, retainUserInput, selectedSubDestination} = this.state;

        let destination = {..._destination};
        delete destination.highlightedTerm;
        const subDestination = _subDestination ? {..._subDestination} : null;

        this.triggerSelectionEdap(destination, subDestination);

        // reset "backspace" and "noSelection" counter after selection
        this.analytics = resetAnalytics(analytics, destination.term);

        let updateState = {
            searchSuggestion: '',
            selectedDestination: destination,
            selectedSubDestination: subDestination,
            selectDestinationIndex: -1,
            selectDestinationSubIndex: -1,
            isTypeaheadInputFocus: false,
            results: false,
            ...(retainUserInput ? {search: destination.term} : {})
        };

        // Add destination to recently searched except PID and destination created from custom user input
        if (!PIDRegExp.test(search) && destination.uuid) {
            addRecentSearch(destination);
        }

        const destinationChanged = isDestinationChanged(
            analytics.prevSearch,
            destination
        );
        const subDestinationChanged = isSubDestinationChanged(
            selectedSubDestination,
            subDestination
        );

        this.setState(updateState, () => {
            let selectedDestination =
                destination.term === '' ? null : destination.term;
            const isLIDSearch = isListingIdSearch(destination);
            selectedDestination = isLIDSearch
                ? destination.listingProps.listingId
                : selectedDestination;
            this.props.onSearchSelected({
                destination: selectedDestination,
                destinationChanged,
                subDestination,
                subDestinationChanged,
                destinationUuid: destination.uuid,
                destinationType: destination.type,
                destinationGaiaId: destination.gaiaId,
                listingProps: isLIDSearch ? destination.listingProps : null
            });
            this.close();
        });

        return true;
    };

    /**
     * getInputDisplayTerm()
     * Helper method to get search term for destination input
     */
    getInputDisplayTerm() {
        const {hideSelectedFilter} = this.props;
        const {
            uistate,
            search,
            selectedDestination,
            selectedSubDestination
        } = this.state;

        if (uistate === UISTATES.SELECT_DESTINATION) {
            return search;
        }

        const term = getDestinationTerm(selectedDestination);
        const filter = getFilterName(selectedSubDestination);

        let displayTerm = term;
        if (!hideSelectedFilter && filter) {
            displayTerm = `${term} > ${filter}`;
        }

        return displayTerm;
    }

    /**
     * handleClear()
     * Clears user search
     */
    handleClear = () => {
        this.setState({
            search: '',
            searchSuggestion: '',
            selectedDestination: null,
            selectedSubDestination: null,
            isTypeaheadInputFocus: true,
            results: false
        });
    };

    /**
     * createContainerRef()
     * Create reference to container element for purpose of animation, etc.
     */
    createContainerRef = (el) => {
        this.containerEl = el;
    };

    /**
     * render()
     * Render the component
     */
    render() {
        return (
            // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
            <div
                role="application"
                onKeyDown={this.handleTopLevelKeyEvents}
                onMouseOver={this.handleOnMouseOver}
                className={classnames(
                    'react-destination-typeahead',
                    this.state.uistate,
                    {
                        'react-destination-typeahead--no-icon': !this.state.icon
                    }
                )}
                ref={this.createContainerRef}
            >
                <SvgDefinitions />
                <AriaStatus {...this.props} {...this.state} />
                <Typeahead
                    {...this.props}
                    {...this.state}
                    onFocus={this.handleFocus}
                    onClick={this.handleTypeaheadClick}
                    onClear={this.handleClear}
                    onChange={this.handleSearchInputChange}
                    onSelectDestination={this.handleSelectDestination}
                    onError={this.handleError}
                    displayTerm={this.getInputDisplayTerm()}
                    rootElem={this.containerEl}
                    isMobile={this.props.isMobile}
                    onBlur={this.props.blur}
                    isTypeaheadInputFocus={
                        this.props.isTypeaheadInputFocus ||
                        this.state.isTypeaheadInputFocus
                    }
                    showSpinner={this.props.showSpinner}
                    showResultIcons={this.props.showResultIcons}
                    showResultLabels={this.props.showResultLabels}
                    skipFilters={skipFilters(this.props.filterType)}
                    filterType={this.props.filterType}
                />
            </div>
        );
    }
}

export default TypeaheadContainer;
export {TypeaheadContainer};
